// utils/socketRooms.ts
export const fileRoom = (userId: string, fileId: string) =>
    `user_${userId}_file_${fileId}`;

// util/getFileExtension.ts
/******************************************************************
 * getFileExtension(filenameOrFile):
 *   - Accepts File, Blob (with a name), or plain string.
 *   - Returns ".pdf", ".docx", "" (empty string when unknown).
 *   - Always lower-case; never includes query-string or fragments.
 *****************************************************************/
export function getFileExtension(
    input: File | Blob & { name?: string } | string,
): string {
    // -------- 0. normalise to a filename string -------------------
    let name: string | undefined;
    let mime:  string | undefined;

    if (typeof input === 'string') {
        name = input;
    } else if ('name' in input) {        // File or Blob-with-name
        name = input.name;
        // @ts-ignore: File has .type, Blob might not
        mime = (input as any).type || undefined;
    } else {
        return '';
    }

    if (!name) return '';
    name = name.trim().toLowerCase()
        .replace(/[?#].*$/, '');       // strip ?query#hash

    // -------- 1. quick rule for dot-files with no extension -------
    // ".bashrc"  -> ''         ".bashrc.old" -> '.old'
    if (name.startsWith('.') && !name.slice(1).includes('.')) {
        return '';
    }

    // -------- 2. path.extname handles messy edge-cases ------------
    try {
        // Works in Node / Bun / vite dev. In the browser this 'require'
        //   block never runs and we fall through to manual logic.
        const { extname } = require('path') as typeof import('path');
        const ext = extname(name).toLowerCase();
        if (isValidExt(ext)) return ext;
    } catch { /* not in Node */ }

    // -------- 3. manual last-dot logic (browser) ------------------
    const lastDot = name.lastIndexOf('.');
    if (lastDot > 0 && lastDot < name.length - 1) {
        const ext = name.slice(lastDot);
        if (isValidExt(ext)) return ext;
    }

    // -------- 4. MIME → extension fall-back -----------------------
    if (mime && typeof mime === 'string') {
        const mapped = mimeToExt[mime.toLowerCase()];
        if (mapped) return mapped;               // explicit mapping

        let subtype = mime.split('/').pop() || '';
        // Drop things like "+xml"  →  "application/fhir+json"
        subtype = subtype.split('+')[0];
        if (subtype && /^[a-z0-9-]{1,16}$/.test(subtype)) {
            return `.${subtype}`;
        }
    }

    // -------- 5. unknown -----------------------------------------
    return '';
}

/* ----------------------------------------------------------------
   Helpers & reference tables
----------------------------------------------------------------- */

function isValidExt(ext: string): boolean {
    // ext includes leading '.' ; reject if only '.', too long, or bad chars
    return (
        /^\.[a-z0-9-]{1,16}$/i.test(ext) &&  // sensible chars + length
        ext !== '._' &&                      // macOS resource forks
        ext !== '.'
    );
}

// High-value MIME ↔︎ extension translations that the generic rule misses.
const mimeToExt: Record<string, string> = {
    // Office
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': '.pptx',
    'application/msword': '.doc',
    'application/vnd.ms-excel': '.xls',
    // Archives
    'application/x-zip-compressed': '.zip',
    'application/x-tar': '.tar',
    'application/x-7z-compressed': '.7z',
    // Media weirdos
    'video/quicktime': '.mov',
    'audio/x-m4a': '.m4a',
    'audio/mpeg': '.mp3',
    // Text
    'text/csv': '.csv',
    'application/json': '.json',
    // …extend as needed
};

